/**
 * First run (UX_CONCEPT.md §5.5): three steps — you on the card, emergency
 * basics, your next trip — asked one question at a time. Each answer is
 * saved as it is given, so leaving halfway keeps what was said.
 */

export const STAGES = [
  "you",
  "blood",
  "allergies",
  "meds",
  "contact",
  "trip",
] as const;
export type Stage = (typeof STAGES)[number] | "done";

/** Which of the three steps a question belongs to. */
const STEP_OF: Record<Stage, 1 | 2 | 3> = {
  you: 1,
  blood: 2,
  allergies: 2,
  meds: 2,
  contact: 2,
  trip: 3,
  done: 3,
};

/** Rough time each question takes, for "1 min left". */
const SECONDS: Record<(typeof STAGES)[number], number> = {
  you: 45,
  blood: 10,
  allergies: 20,
  meds: 20,
  contact: 30,
  trip: 40,
};

export function stepOf(stage: Stage): 1 | 2 | 3 {
  return STEP_OF[stage];
}

/** The emergency-basics question number, 1–4, or null outside step 2. */
export function basicsNumber(stage: Stage): number | null {
  const basics: Stage[] = ["blood", "allergies", "meds", "contact"];
  const i = basics.indexOf(stage);
  return i < 0 ? null : i + 1;
}

/** Whole minutes left in the current step, at least 1. */
export function minutesLeft(stage: Stage): number {
  if (stage === "done") return 0;
  const step = stepOf(stage);
  const from = STAGES.indexOf(stage);
  const seconds = STAGES.slice(from)
    .filter((s) => stepOf(s) === step)
    .reduce((sum, s) => sum + SECONDS[s], 0);
  return Math.max(1, Math.ceil(seconds / 60));
}

/** How full each of the three progress bars is, 0–1. */
export function progress(stage: Stage): [number, number, number] {
  if (stage === "done") return [1, 1, 1];
  const step = stepOf(stage);
  const inStep = STAGES.filter((s) => stepOf(s) === step);
  const fraction =
    inStep.indexOf(stage as (typeof STAGES)[number]) / inStep.length;
  return [1, 2, 3].map((n) => (n < step ? 1 : n === step ? fraction : 0)) as [
    number,
    number,
    number,
  ];
}

export function nextStage(stage: Stage): Stage {
  if (stage === "done") return "done";
  return STAGES[STAGES.indexOf(stage) + 1] ?? "done";
}

export const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

/** The allergies a responder most often has to know about. */
export const COMMON_ALLERGIES = [
  "Penicillin",
  "Peanuts",
  "Shellfish",
  "Ibuprofen",
  "Sulfa drugs",
  "Latex",
];

export const RELATIONSHIPS = [
  "Partner",
  "Parent",
  "Child",
  "Sibling",
  "Friend",
] as const;

/** The same rule the server applies (users-fastify emergencyContactsFrom). */
export function isDialable(phone: string): boolean {
  return /^\+?[0-9 ()./-]{4,40}$/.test(phone.trim());
}

/**
 * Splits "latex, bee stings" into entries and joins them with the picked
 * chips and what is already on record, without repeats in any case.
 */
export function mergeAllergies(
  existing: string[],
  picked: string[],
  other: string,
): string[] {
  const all = [
    ...existing,
    ...picked,
    ...other.split(/[,;\n]/).map((s) => s.trim()),
  ].filter(Boolean);
  const seen = new Set<string>();
  return all.filter((a) => {
    const key = a.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
